import { API_BASE_URL } from '../config';
import {
  HsmCampaignMessage,
  HsmMessageLog,
  HsmCampaignStats,
  HsmDayStats,
  HsmCampaignMessageRequest,
  HsmTestMessageRequest
} from '../types';

// ==================== CAMPAIGN MESSAGES ====================

export async function fetchCampaignMessages(
  accessToken: string
): Promise<HsmCampaignMessage[]> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaign-messages`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar mensagens da campanha');
  }

  const payload = await response.json();
  return payload.data || [];
}

export async function fetchActiveCampaignMessages(accessToken: string): Promise<HsmCampaignMessage[]> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaign-messages/active`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar mensagens ativas');
  }

  const payload = await response.json();
  return payload.data || [];
}

export async function fetchCampaignMessageByDay(
  day: number,
  accessToken: string
): Promise<HsmCampaignMessage | null> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaign-messages/day/${day}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar mensagem');
  }

  const payload = await response.json();
  return payload.data;
}

export async function createCampaignMessage(
  data: HsmCampaignMessageRequest,
  accessToken: string
): Promise<HsmCampaignMessage> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaign-messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao criar mensagem');
  }

  const payload = await response.json();
  return payload.data;
}

export async function updateCampaignMessage(
  messageId: number,
  data: Partial<HsmCampaignMessageRequest>,
  accessToken: string
): Promise<HsmCampaignMessage> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaign-messages/${messageId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao atualizar mensagem');
  }

  const payload = await response.json();
  return payload.data;
}

export async function deleteCampaignMessage(
  messageId: number,
  accessToken: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaign-messages/${messageId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao excluir mensagem');
  }
}

// ==================== CAMPAIGN EXECUTION ====================
// Note: These use the correct backend endpoints at /hsm/campaigns/run
// For running specific campaigns by ID, use runCampaignById from hsm-campaigns.service.ts

export async function runCampaign(accessToken: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/run`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao executar campanhas');
  }

  const payload = await response.json();
  return payload.data || payload;
}

export async function runCampaignDay(
  day: number,
  accessToken: string
): Promise<{ message: string }> {
  // Note: The backend runs campaigns by ID, not by day number
  // This endpoint no longer exists - use runCampaignById for specific campaigns
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/run`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || `Erro ao executar campanhas`);
  }

  const payload = await response.json();
  return payload.data || payload;
}

// ==================== LOGS & STATS ====================

export async function fetchMessageLogs(
  accessToken: string,
  limit: number = 100
): Promise<HsmMessageLog[]> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/logs?limit=${limit}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar logs');
  }

  const payload = await response.json();
  return payload.data || [];
}

export async function fetchCampaignStats(accessToken: string): Promise<HsmCampaignStats> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/stats`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar estatísticas');
  }

  const payload = await response.json();
  return payload.data;
}

export async function fetchDayStats(
  day: number,
  accessToken: string
): Promise<HsmDayStats> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaign/stats/day/${day}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || `Erro ao carregar estatísticas do dia ${day}`);
  }

  const payload = await response.json();
  return payload.data;
}

// ==================== TEST ====================

export async function sendTestMessage(
  data: HsmTestMessageRequest,
  accessToken: string
): Promise<{ success: boolean; messageId?: string; error?: string }> {
  const response = await fetch(`${API_BASE_URL}/hsm/test/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao enviar mensagem de teste');
  }

  const payload = await response.json();
  return payload.data;
}

// ==================== CONFIG ====================

export interface WhatsAppConfig {
  phoneNumberId: string;
  accessTokenConfigured: boolean;
  wabaId: string | null;
  apiUrl: string;
}

export async function fetchWhatsAppConfig(accessToken: string): Promise<WhatsAppConfig> {
  const response = await fetch(`${API_BASE_URL}/hsm/test/config`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar configuração');
  }

  const payload = await response.json();
  return payload.data;
}
